import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Car, 
  Truck, 
  Bus, 
  Bike,
  ChevronDown,
  Gauge,
  Activity,
  Filter,
  SortAsc,
  SortDesc
} from 'lucide-react';
import { Vehicle } from '../types';
import { getSpeedColor } from '../utils/helpers';

interface VehicleListProps {
  vehicles: Vehicle[];
  onVehicleClick?: (vehicle: Vehicle) => void;
}

type SortField = 'id' | 'avg_speed' | 'max_speed' | 'detections_count';
type SortOrder = 'asc' | 'desc';

const vehicleIcons: Record<string, React.ElementType> = {
  car: Car,
  truck: Truck,
  bus: Bus,
  motorcycle: Bike,
  bicycle: Bike,
  default: Car
};

// Default colors if not provided by backend
const DEFAULT_COLORS = [
  [0, 0, 255],      // Blue
  [0, 255, 0],      // Green
  [255, 0, 0],      // Red
  [0, 255, 255],    // Cyan
  [255, 0, 255],    // Magenta
  [255, 255, 0],    // Yellow
  [255, 128, 0],    // Orange
  [128, 0, 255],    // Purple
  [0, 255, 128],    // Spring Green
  [255, 0, 128],    // Pink
];

function getVehicleColorStyle(vehicle: Vehicle): string {
  if (vehicle.color) {
    return `rgb(${vehicle.color[0]}, ${vehicle.color[1]}, ${vehicle.color[2]})`;
  }
  // Fallback based on vehicle number
  try {
    const num = parseInt(vehicle.id.split('_')[1]) || 0;
    const color = DEFAULT_COLORS[num % DEFAULT_COLORS.length];
    return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
  } catch {
    return 'rgb(99, 102, 241)';
  }
}

export function VehicleList({ vehicles, onVehicleClick }: VehicleListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('max_speed');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [filterType, setFilterType] = useState<string>('all');

  const vehicleTypes = useMemo(() => {
    const types = new Set(vehicles.map(v => v.vehicle_type || 'unknown'));
    return ['all', ...Array.from(types)];
  }, [vehicles]);

  const sortedVehicles = useMemo(() => {
    let filtered = vehicles;
    
    if (filterType !== 'all') {
      filtered = vehicles.filter(v => (v.vehicle_type || 'unknown') === filterType);
    }

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'id':
          comparison = a.id.localeCompare(b.id);
          break;
        case 'avg_speed':
          comparison = a.avg_speed - b.avg_speed;
          break;
        case 'max_speed':
          comparison = a.max_speed - b.max_speed;
          break;
        case 'detections_count':
          comparison = a.detections_count - b.detections_count;
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [vehicles, sortField, sortOrder, filterType]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = sortOrder === 'asc' ? SortAsc : SortDesc;

  if (vehicles.length === 0) {
    return (
      <div className="text-center py-12">
        <Car className="w-12 h-12 text-dark-600 mx-auto mb-4" />
        <p className="text-dark-400">No vehicles detected</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4 p-4 glass rounded-xl">
        {/* Filter by type */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-dark-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-dark-800 border border-dark-600 rounded-lg px-3 py-1.5 text-sm text-dark-200 focus:outline-none focus:border-primary-500"
          >
            {vehicleTypes.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Sort buttons */}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-dark-500">Sort by:</span>
          {[
            { field: 'max_speed' as SortField, label: 'Speed' },
            { field: 'detections_count' as SortField, label: 'Detections' }
          ].map(({ field, label }) => (
            <button
              key={field}
              onClick={() => toggleSort(field)}
              className={`
                flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors
                ${sortField === field 
                  ? 'bg-primary-500/20 text-primary-400' 
                  : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
                }
              `}
            >
              {label}
              {sortField === field && <SortIcon className="w-3 h-3" />}
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-dark-500">
        Showing {sortedVehicles.length} of {vehicles.length} vehicles
      </p>

      {/* Vehicle list */}
      <div className="space-y-2">
        <AnimatePresence>
          {sortedVehicles.map((vehicle, index) => {
            const isExpanded = expandedId === vehicle.id;
            const VehicleIcon = vehicleIcons[vehicle.vehicle_type || 'default'] || vehicleIcons.default;
            const speedColor = getSpeedColor(vehicle.max_speed);
            const vehicleColor = getVehicleColorStyle(vehicle);
            const vehicleNum = parseInt(vehicle.id.split('_')[1]) || 0;

            return (
              <motion.div
                key={vehicle.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
                className="glass rounded-xl overflow-hidden"
              >
                {/* Main row */}
                <div
                  onClick={() => {
                    setExpandedId(isExpanded ? null : vehicle.id);
                    onVehicleClick?.(vehicle);
                  }}
                  className="flex items-center gap-4 p-4 cursor-pointer hover:bg-dark-700/30 transition-colors"
                >
                  {/* Vehicle number badge */}
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
                    style={{ backgroundColor: vehicleColor }}
                  >
                    {vehicleNum}
                  </div>

                  {/* Vehicle icon */}
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${vehicleColor}20` }}
                  >
                    <VehicleIcon 
                      className="w-5 h-5" 
                      style={{ color: vehicleColor }}
                    />
                  </div>

                  {/* Vehicle info */}
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-dark-100" style={{ color: vehicleColor }}>
                        Vehicle #{vehicleNum}
                      </span>
                      {vehicle.vehicle_type && (
                        <span className="px-2 py-0.5 text-xs bg-dark-700 text-dark-300 rounded-full capitalize">
                          {vehicle.vehicle_type}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-dark-400">
                      <span className="flex items-center gap-1">
                        <Gauge className="w-3 h-3" />
                        {vehicle.avg_speed.toFixed(1)} km/h avg
                      </span>
                      <span className="flex items-center gap-1">
                        <Activity className="w-3 h-3" />
                        {vehicle.detections_count} detections
                      </span>
                    </div>
                  </div>

                  {/* Max speed badge */}
                  <div 
                    className="px-4 py-2 rounded-lg font-mono font-bold text-lg"
                    style={{ 
                      backgroundColor: `${speedColor}20`,
                      color: speedColor
                    }}
                  >
                    {vehicle.max_speed.toFixed(0)}
                    <span className="text-xs ml-1 opacity-70">km/h</span>
                  </div>

                  {/* Expand button */}
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    className="text-dark-400"
                  >
                    <ChevronDown className="w-5 h-5" />
                  </motion.div>
                </div>

                {/* Expanded details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-dark-700"
                    >
                      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-dark-500 mb-1">Average Speed</p>
                          <p className="text-lg font-mono text-dark-200">
                            {vehicle.avg_speed.toFixed(1)} km/h
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-dark-500 mb-1">Minimum Speed</p>
                          <p className="text-lg font-mono text-dark-200">
                            {vehicle.min_speed.toFixed(1)} km/h
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-dark-500 mb-1">Maximum Speed</p>
                          <p className="text-lg font-mono text-dark-200" style={{ color: speedColor }}>
                            {vehicle.max_speed.toFixed(1)} km/h
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-dark-500 mb-1">Confidence</p>
                          <p className="text-lg font-mono text-dark-200">
                            {(vehicle.avg_confidence * 100).toFixed(1)}%
                          </p>
                        </div>
                      </div>

                      {/* Speed bar visualization */}
                      <div className="px-4 pb-4">
                        <div className="flex items-center gap-2 text-xs text-dark-500 mb-2">
                          <span>Speed Range</span>
                          <span className="flex-grow h-px bg-dark-700" />
                        </div>
                        <div className="relative h-2 bg-dark-800 rounded-full overflow-hidden">
                          <div
                            className="absolute h-full rounded-full"
                            style={{
                              left: `${(vehicle.min_speed / 150) * 100}%`,
                              right: `${100 - (vehicle.max_speed / 150) * 100}%`,
                              background: `linear-gradient(90deg, ${getSpeedColor(vehicle.min_speed)}, ${speedColor})`
                            }}
                          />
                          <div 
                            className="absolute h-4 w-1 -top-1 bg-white rounded-full"
                            style={{ left: `${(vehicle.avg_speed / 150) * 100}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-1 text-xs text-dark-600">
                          <span>0</span>
                          <span>75</span>
                          <span>150 km/h</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default VehicleList;
